import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "./app";
import {
	TOrder,
	TNewProduct,
	TOrganizationBalanceEntry,
	TOrganizationBalanceRollup,
} from "@jsdev_ninja/core";
import { reportError } from "src/lib/observability/reportError";

export type CreateProductResult =
	| { success: true; data: { id: string } }
	| { success: false; reason: "duplicate-sku" | "invalid" | "unauthorized" | "unknown"; message: string };

const functions = getFunctions(app);

async function init() {
	try {
		const func = httpsCallable(functions, "appInit");

		const response = await func();
		return { success: true, data: response.data };
	} catch (error: any) {
		const code = error.code;
		reportError(error, { operation: "firebase.appInit", code });
		return { success: false, data: null };
	}
}

export type LogPayload = {
	message: string;
	severity: "DEBUG" | "INFO" | "NOTICE" | "WARNING" | "ERROR" | "CRITICAL" | "ALERT" | "EMERGENCY";
	userId?: string;
	companyId?: string;
	storeId?: string;
	tenantId?: string;
	[key: string]: any; // Allows any additional properties
};

async function uiLogs(payload: LogPayload) {
	try {
		const func = httpsCallable(functions, "uiLogs");

		const response = await func(payload);
		return { success: true, data: response.data };
	} catch (error: any) {
		const code = error.code;
		reportError(error, { operation: "firebase.uiLogs", code });
		return { success: false, data: null };
	}
}

// dupliacte form functions/src/services/ezCountService/index.ts
export enum VAT_TYPE {
	PRE = "PRE",
	INC = "INC",
	NON = "NON",
}
export type Params = {
	transaction_id: string;
	customer_name: string;
	customer_email: string;
	customer_address?: string;
	customer_phone?: string;
	description?: string;
	parent?: string; // parens docs (1,2,3,4)
	allocationNumber?: string; // מספר הקצאה — required by EZcount for invoices >= 25,000 ILS
	cc_emails?: string[];
	item?: {
		details: string;
		price: number;
		amount: number;
		vat_type: VAT_TYPE;
	}[];
	price_total?: number;
	date?: string; // (DD/MM/YYYY)
};

export enum DOC_TYPE {
	ORDER = 100, // הזמנה (Order)
	DELIVERY = 200, // תעודת משלוח (Delivery)
	RETURN = 210, // תעודת החזרה (Return)
	PROFORMA_INVOICE = 300, // חשבונית עסקה (Proforma Invoice)
	TAX_INVOICE = 305, // חשבונית מס(Tax invoice)
	INVOICE_RECEIPT = 320, // חשבונית מס קבלה(Invoice Receipt)
	CREDIT_INVOICE = 330, // חשבונית זיכוי(Credit invoice)
	RECEIPT = 400, // קבלה(Receipt)
	RECEIPT_DONATION = 405, // קבלה על תרומה(Receipt for donation)
	PURCHASE_ORDER = 500, // הזמנת רכש(Purchase order)
	BID = 9999, // הצעת מחיר(Bid)
	DEPOSIT_APPROVAL = 9998, // קבלת פקדון (Deposit Approval)
	DEPOSIT_RELEASE = 9997, // קבלת פקדון (Deposit Release)
}

async function createInvoice(
	storeId: string,
	{ orders, params }: { orders: TOrder[]; params: Params },
) {
	try {
		const func = httpsCallable(functions, "createInvoice");
		const response = await func({ orders, params, storeId });
		return { success: true, data: response.data };
	} catch (error: any) {
		const code = error.code;
		const message = error.message;
		const details = error.details;
		reportError(code, message, details);
		return { success: false, data: null };
	}
}

async function createPayment({ order, isJ5 }: { order: TOrder; isJ5?: boolean }) {
	try {
		const func = httpsCallable(functions, "createPayment");

		const response = await func({ order, isJ5 });
		return { success: true, data: response.data };
	} catch (error: any) {
		const code = error.code;
		const message = error.message;
		const details = error.details;
		reportError(code, message, details);
		return { success: false, data: null };
	}
}
async function createPaymentRedirect({
	order,
	isJ5,
	origin,
}: {
	order: TOrder;
	isJ5?: boolean;
	origin: string;
}) {
	try {
		const func = httpsCallable(functions, "createPaymentRedirect");
		const response = await func({ order, isJ5, origin });
		return {
			success: true,
			data: response.data as {
				success: boolean;
				url?: string;
				token?: string;
				error?: string;
			},
		};
	} catch (error: unknown) {
		const e = error as { code?: string; message?: string; details?: unknown };
		reportError(e.code, e.message, e.details);
		return { success: false, data: null };
	}
}

// --- Invoice payment links (parallel to order links; new endpoints) ---

async function createHypInvoicePaymentLink({
	orderId,
	origin,
}: {
	orderId: string;
	origin: string;
}) {
	try {
		const func = httpsCallable(functions, "createHypInvoicePaymentLink");
		const response = await func({ orderId, origin });
		return {
			success: true,
			data: response.data as {
				success: boolean;
				url?: string;
				token?: string;
				expiresAt?: number;
				reused?: boolean;
				error?: string;
			},
		};
	} catch (error: unknown) {
		const e = error as { code?: string; message?: string; details?: unknown };
		reportError(e.code, e.message, e.details);
		return { success: false, data: null };
	}
}

async function getInvoicePaymentLink({ token }: { token: string }) {
	try {
		const func = httpsCallable(functions, "getInvoicePaymentLink");
		const response = await func({ token });
		return {
			success: true,
			data: response.data as {
				success: boolean;
				formAction?: string;
				formFields?: Record<string, string>;
				display?: {
					invoiceDocNumber: string;
					customerName: string;
					amountAgorot: number;
				};
				error?: string;
			},
		};
	} catch (error: unknown) {
		const e = error as { code?: string; message?: string; details?: unknown };
		reportError(e.code, e.message, e.details);
		return { success: false, data: null };
	}
}

async function recordHypInvoicePayment(params: {
	token: string;
	companyId: string;
	storeId: string;
	Id: string;
	CCode: string;
	Amount: string;
	Order: string;
	ACode?: string;
	Sign?: string;
	Masof?: string;
	rawResponse: Record<string, unknown>;
}) {
	try {
		const func = httpsCallable(functions, "recordHypInvoicePayment");
		const response = await func(params);
		return {
			success: true,
			data: response.data as {
				success: boolean;
				transactionId?: string;
				error?: string;
			},
		};
	} catch (error: unknown) {
		const e = error as { code?: string; message?: string; details?: unknown };
		reportError(e.code, e.message, e.details);
		return { success: false, data: null };
	}
}

async function getPaymentRedirect({ token }: { token: string }) {
	try {
		const func = httpsCallable(functions, "getPaymentRedirect");
		const response = await func({ token });
		return {
			success: true,
			data: response.data as {
				success: boolean;
				formAction?: string;
				formFields?: Record<string, string>;
				error?: string;
			},
		};
	} catch (error: unknown) {
		const e = error as { code?: string; message?: string; details?: unknown };
		reportError(e.code, e.message, e.details);
		return { success: false, data: null };
	}
}

async function chargeOrder({ order }: { order: TOrder }) {
	try {
		const func = httpsCallable(functions, "chargeOrder");

		const response = await func({ order });
		return { success: true, data: response.data };
	} catch (error: any) {
		const code = error.code;
		const message = error.message;
		const details = error.details;
		reportError(code, message, details);
		return { success: false, data: null };
	}
}

// ── New ledger flow (server-side, signature-verified) ──────────────────────
// Customer J5 checkout: the server loads the order, signs the HYP form, and
// returns it. Replaces the legacy client-trusting `createPayment`.
async function createHypCheckoutPayment({
	orderId,
	companyId,
	storeId,
	isJ5 = true,
}: {
	orderId: string;
	companyId: string;
	storeId: string;
	isJ5?: boolean;
}) {
	try {
		const func = httpsCallable(functions, "createHypCheckoutPayment");
		const response = await func({ orderId, companyId, storeId, isJ5 });
		return {
			success: true,
			data: response.data as {
				success: boolean;
				formAction?: string;
				formFields?: Record<string, string>;
				error?: string;
			},
		};
	} catch (error: unknown) {
		const e = error as { code?: string; message?: string; details?: unknown };
		reportError(e.code, e.message, e.details);
		return { success: false, data: null };
	}
}

// Records a verified J5 authorization from the HYP browser redirect. The server
// re-verifies the signature with HYP before writing; the order is flipped to
// paid by the `onTransactionPostedMarkOrderPaid` subscriber — never the client.
async function recordHypJ5Auth(input: {
	companyId: string;
	storeId: string;
	Id: string;
	CCode: string;
	Amount: string;
	Order: string;
	/** HYP does not echo Masof on the redirect; the server verifies with its own */
	Masof?: string;
	/** J5 token — present on holds, may be absent on a direct charge */
	UID?: string;
	ACode?: string;
	Sign?: string;
	last4?: string;
	clientName?: string;
	email?: string;
	reference: { type: "order"; id: string };
	rawResponse: Record<string, unknown>;
	// No payer: the server derives it from the referenced order.
}) {
	try {
		const func = httpsCallable(functions, "recordHypJ5Auth");
		const response = await func(input);
		return {
			success: true,
			data: response.data as {
				success: boolean;
				transactionId?: string;
				error?: string;
			},
		};
	} catch (error: unknown) {
		const e = error as { code?: string; message?: string; details?: unknown };
		reportError(e.code, e.message, e.details);
		return { success: false, data: null };
	}
}

async function deleteClient(clientId: string) {
	try {
		const func = httpsCallable(functions, "deleteClient");
		const response = await func({ clientId });
		return response.data as { success: boolean; authDeleted?: boolean; error?: string };
	} catch (error: any) {
		const code = error.code;
		const message = error.message;
		reportError("deleteClient failed", code, message);
		return { success: false as const, error: message ?? "unknown" };
	}
}

async function createDeliveryNote({
	order,
	options,
}: {
	order: TOrder;
	options?: { date?: number; sendEmailToClient?: boolean };
}) {
	try {
		const func = httpsCallable(functions, "createDeliveryNote");

		const response = await func({ order, options });
		return { success: true, data: response.data };
	} catch (error: any) {
		const code = error.code;
		const message = error.message;
		const details = error.details;
		reportError(code, message, details);
		return { success: false, data: null, error: message };
	}
}

export type ChatHistoryItem = {
	role: "user" | "bot";
	text: string;
};

export type ChatbotResult = {
	success: boolean;
	data: { content: string | null } | null;
	error?: string;
};

async function chatbotChat(
	prompt: string,
	history: ChatHistoryItem[],
	context: { cartId?: string; storeId?: string; companyId?: string },
): Promise<ChatbotResult> {
	try {
		const func = httpsCallable(functions, "chatbotApi");
		const response = await func({ prompt, history, context });
		const data = response.data as { content: string | null };
		return { success: true, data };
	} catch (error: any) {
		const message = error?.message ?? "Unknown error";
		reportError("chatbotChat", error);
		return { success: false, data: null, error: message };
	}
}

/**
 * Record an external (non-HYP) money movement in the ledger — cash, bank
 * transfer, etc. Posts a `manual` transaction which the ledger fans out to
 * mark the referenced order paid and reduce the org's budget debt.
 * `amount` MUST be integer agorot (convert shekels at the call site).
 */
async function postManualTransaction(params: {
	amount: number; // integer agorot
	idempotencyKey: string;
	// "invoice" added to support admin payment recording against a specific invoice.
	reference?: { type: "order" | "refund" | "adjustment" | "invoice"; id: string };
	payer?: { organizationId?: string; clientId?: string; billingAccountId?: string };
}) {
	const func = httpsCallable(functions, "postManualTransaction");
	const res = await func(params);
	return res.data as { success: boolean; transactionId?: string; error?: string };
}

/** Shape returned by getOpenInvoices for a single unpaid invoice row. */
export type OpenInvoiceRow = {
	orderId: string;
	invoiceUuid: string;
	invoiceNumber: string;
	invoicePdfLink: string;
	issueDate: number; // epoch millis
	total: number; // shekels (ILS)
	displayName: string; // REQUIRED — name that appears on the invoice; backend excludes nameless rows
	organizationId?: string; // kept for company-filter dropdown, not for display
};

/**
 * Admin: list all orders with an unpaid invoice for the active store.
 * Returns orders where ezInvoice.success == true AND neither invoicePaidAt
 * nor ezReceipt is set. Sorted newest first.
 */
async function getOpenInvoices(params?: { fromDate?: number; toDate?: number }) {
	const func = httpsCallable(functions, "getOpenInvoices");
	const res = await func(params ?? {});
	return res.data as { success: boolean; data?: OpenInvoiceRow[]; error?: string };
}

type PaymentMethod = "cash" | "check" | "bank_transfer" | "credit_card";

type RecordInvoicePaymentParams = {
	orderId: string;
	paymentMethod: PaymentMethod;
	paymentDate: number; // epoch millis
	note?: string;
	/**
	 * Idempotency key. Convention: `inv-pay-{orderId}`.
	 * Deterministic key means double-clicks are no-ops — single full payment per invoice.
	 */
	idempotencyKey: string;
};

type RecordInvoicePaymentResult =
	| { success: true; receipt: { doc_uuid: string; pdf_link: string; doc_number: string } }
	| {
			success: false;
			error: string;
			code:
				| "invoice_missing"
				| "already_paid"
				| "ezcount_failed"
				| "ledger_failed"
				| "tenant_mismatch"
				| "amount_mismatch";
	  };

/**
 * Admin: record a full payment against an open invoice.
 * Creates an EZcount receipt and marks the invoice paid on the order doc.
 * Partial payments are NOT supported — this always pays the full invoice total.
 */
async function recordInvoicePayment(
	params: RecordInvoicePaymentParams,
): Promise<RecordInvoicePaymentResult> {
	const func = httpsCallable(functions, "recordInvoicePayment");
	const res = await func(params);
	return res.data as RecordInvoicePaymentResult;
}

type CreateCreditNoteParams = {
	organizationId: string;
	/** Set when the credit is linked to an invoice — an order carrying that EZcount invoice */
	orderId?: string | null;
	amount: number; // integer agorot, > 0 (gross incl. VAT)
	reason: string; // Hebrew reason label (1-200 chars)
	note?: string | null; // max 500
	creditDate: number; // epoch millis
	/**
	 * Idempotency key. Convention: `crd-{crypto.randomUUID()}`, generated once
	 * per modal open so retries after network errors are idempotent.
	 */
	idempotencyKey: string;
};

type CreateCreditNoteResult =
	| {
			success: true;
			creditNote: { id: string; doc_uuid: string; doc_number: string; pdf_link: string };
	  }
	| {
			success: false;
			error: string;
			code: "invalid_input" | "organization_missing" | "invoice_missing" | "ezcount_failed" | "ar_failed";
	  };

/**
 * Admin: issue a credit note (תעודת זיכוי) against an organization's AR balance.
 * Creates an EZcount credit invoice (doc_type 330) and writes a settlement
 * entry to the organization balance ledger. Never touches the cash ledger.
 */
async function createCreditNote(params: CreateCreditNoteParams): Promise<CreateCreditNoteResult> {
	const func = httpsCallable(functions, "createCreditNote");
	const res = await func(params);
	return res.data as CreateCreditNoteResult;
}

/**
 * Admin: AR rollup + entry ledger for one organization (customer ledger card).
 * Read-only. Auth (admin claim) + tenant scope are enforced server-side.
 * `rollup` is the O(1) balance cache; `entries` is the append-only debit/credit ledger.
 */
async function getOrganizationBalance(params: {
	organizationId: string;
	fromMillis?: number;
	toMillis?: number;
}) {
	const func = httpsCallable(functions, "getOrganizationBalance");
	const res = await func(params);
	return res.data as {
		success: boolean;
		error?: string;
		data?: {
			rollup: TOrganizationBalanceRollup | null;
			entries: TOrganizationBalanceEntry[];
		};
	};
}

/**
 * Callable errors reach the client as a FirebaseError whose `code` is prefixed
 * with the service name ("functions/already-exists"), never the bare gRPC
 * status. Strip the prefix so comparisons against the status match.
 */
function normalizeCallableErrorCode(code: unknown): string {
	return typeof code === "string" ? code.replace(/^functions\//, "") : "";
}

async function createProduct(product: TNewProduct): Promise<CreateProductResult> {
	try {
		const func = httpsCallable(functions, "createProduct");
		const response = await func(product);
		// The callable reports auth/config failures in the payload with HTTP 200
		// instead of throwing, so a resolved call is NOT proof the product was
		// written. Casting the payload to the success shape made every such
		// failure look like a success: the admin was navigated back to the
		// product list and nothing had been created.
		const payload = response.data as
			| { success?: boolean; data?: { id?: string }; error?: string }
			| null
			| undefined;

		if (!payload?.success || !payload.data?.id) {
			const error = payload?.error ?? "";
			const reason: "unauthorized" | "unknown" =
				error === "Unauthorized" || error === "missing_token_claims" ? "unauthorized" : "unknown";
			reportError("createProduct rejected", { operation: "createProduct", reason, error });
			return { success: false, reason, message: error || "Product was not created" };
		}

		return { success: true, data: { id: payload.data.id } };
	} catch (error: any) {
		const code = normalizeCallableErrorCode(error?.code);
		const message: string = error?.message ?? "Unknown error";
		if (code === "already-exists") {
			return { success: false, reason: "duplicate-sku", message };
		}
		if (code === "invalid-argument") {
			return { success: false, reason: "invalid", message };
		}
		if (code === "unauthenticated" || code === "permission-denied") {
			return { success: false, reason: "unauthorized", message };
		}
		reportError("createProduct failed", { operation: "createProduct", code, message });
		return { success: false, reason: "unknown", message };
	}
}

async function createOrder(params: { order: TOrder; companyId?: string; storeId?: string }) {
	try {
		const func = httpsCallable(functions, "createOrder");
		const response = await func(params);
		const data = response.data as { success: boolean; data?: { id: string }; error?: string };
		return data;
	} catch (error: any) {
		const code: string = error.code ?? "";
		const message: string = error.message ?? "Unknown error";
		reportError("createOrder failed", code, message);
		return { success: false, error: message };
	}
}

async function updateOrder(params: { orderId: string; updates: Partial<TOrder>; companyId?: string; storeId?: string }) {
	try {
		const func = httpsCallable(functions, "updateOrder");
		const response = await func(params);
		const data = response.data as { success: boolean; data?: { id: string }; error?: string };
		return data;
	} catch (error: any) {
		const code: string = error.code ?? "";
		const message: string = error.message ?? "Unknown error";
		reportError("updateOrder failed", code, message);
		return { success: false, error: message };
	}
}

export const api = {
	init,
	deleteClient,
	createPayment,
	createHypCheckoutPayment,
	recordHypJ5Auth,
	chargeOrder,
	createPaymentRedirect,
	getPaymentRedirect,
	createHypInvoicePaymentLink,
	getInvoicePaymentLink,
	recordHypInvoicePayment,
	uiLogs,
	createInvoice,
	createDeliveryNote,
	chatbotChat,
	postManualTransaction,
	getOrganizationBalance,
	createProduct,
	createOrder,
	getOpenInvoices,
	recordInvoicePayment,
	createCreditNote,
	updateOrder,
};
